import PaylensQuery from '@/services/query/paylens/PaylensQuery.service';
import { getOriginFromIframeSrc } from '@/utils/iframeAccessTokenBridge';

/** Embed iframe → backoffice parent */
export const PAYLENS_READY_MESSAGE_TYPE = 'paylens:ready' as const;
export const PAYLENS_REFRESH_TOKEN_MESSAGE_TYPE = 'paylens:refresh-token' as const;

/** Backoffice parent → embed iframe */
export const PAYLENS_TOKEN_MESSAGE_TYPE = 'paylens:token' as const;
export const PAYLENS_AUTH_FAILED_MESSAGE_TYPE = 'paylens:auth-failed' as const;

/** Embed iframe'e token postMessage gönderildi (token detail'de yok). */
export const PAYLENS_EMBED_TOKEN_DOM_EVENT = 'backoffice:paylens-embed-token-postmessage';

/** Embed iframe'den ready / refresh-token alındı. */
export const PAYLENS_EMBED_INBOUND_MESSAGE_DOM_EVENT = 'backoffice:paylens-embed-inbound-postmessage';

/** bo-embed.js ile aynı: süre dolmadan 3 dk önce yenile */
const REFRESH_SKEW_SECONDS = 180;

export type PaylensAccessTokenSnapshot = {
    token: string;
    expiresAt: number;
    userId?: string;
};

export type PaylensEmbedTokenRequestReason = 'embed-load' | 'embed-ready' | 'embed-refresh';

export type PaylensEmbedTokenDomEventDetail = {
    targetOrigin: string;
    hasToken: boolean;
};

export type PaylensEmbedInboundDomEventDetail = {
    targetOrigin: string;
    messageType: typeof PAYLENS_READY_MESSAGE_TYPE | typeof PAYLENS_REFRESH_TOKEN_MESSAGE_TYPE;
};

export type PaylensTokenPostMessagePayload = {
    type: typeof PAYLENS_TOKEN_MESSAGE_TYPE;
    token: string;
    expiresAt: number;
    userId?: string;
};

export type PaylensAuthFailedPostMessagePayload = {
    type: typeof PAYLENS_AUTH_FAILED_MESSAGE_TYPE;
    message: string;
};

let cachedAccessToken: PaylensAccessTokenSnapshot | null = null;
let tokenRequestPromise: Promise<PaylensAccessTokenSnapshot> | null = null;

function shouldLogPaylensEmbedTokenForTest(): boolean {
    const envName = (import.meta.env.VITE_ENVIRONMENT || '').toString().toLowerCase();
    return envName !== 'prod' && envName !== 'master';
}

function logPaylensEmbedTokenForTest(
    snapshot: PaylensAccessTokenSnapshot,
    meta?: { reason?: PaylensEmbedTokenRequestReason; fromCache?: boolean }
): void {
    if (!shouldLogPaylensEmbedTokenForTest()) {
        return;
    }
    console.info('[Paylens] embed token (test)', snapshot, {
        userId: snapshot.userId,
        expiresAt: snapshot.expiresAt,
        expiresAtIso: new Date(snapshot.expiresAt * 1000).toISOString(),
        reason: meta?.reason,
        fromCache: meta?.fromCache ?? false,
    });
}

function isCachedTokenValid(snapshot: PaylensAccessTokenSnapshot | null): snapshot is PaylensAccessTokenSnapshot {
    if (!snapshot?.token) {
        return false;
    }
    const now = Math.floor(Date.now() / 1000);
    return now < snapshot.expiresAt - REFRESH_SKEW_SECONDS;
}

function mapBffResponse(data: Record<string, unknown>): PaylensAccessTokenSnapshot {
    const token = (data.token ?? data.Token) as string | undefined;
    const userId = (data.userId ?? data.UserId) as string | undefined;
    let expiresAt = (data.expiresAt ?? data.ExpiresAt) as number | undefined;
    const ttlMinutes = (data.ttlMinutes ?? data.TtlMinutes) as number | undefined;

    if (!token) {
        throw new Error('Paylens embed token response is missing token');
    }

    if (expiresAt == null && ttlMinutes != null) {
        expiresAt = Math.floor(Date.now() / 1000) + ttlMinutes * 60;
    }
    if (expiresAt == null) {
        expiresAt = Math.floor(Date.now() / 1000) + 720;
    }

    return { token, expiresAt, userId };
}

export function clearPaylensCachedAccessToken(): void {
    cachedAccessToken = null;
}

async function fetchPaylensEmbedTokenFromBff(): Promise<PaylensAccessTokenSnapshot> {
    const data = (await PaylensQuery.GetEmbedToken()) as Record<string, unknown>;
    const snapshot = mapBffResponse(data);
    cachedAccessToken = snapshot;
    return snapshot;
}

/**
 * Geçerli cache varsa döner; yoksa BFF embed-token endpoint'ini çağırır (bo-simulator modeli).
 */
export function getValidPaylensEmbedToken(options?: { forceRefresh?: boolean }): Promise<PaylensAccessTokenSnapshot> {
    if (options?.forceRefresh) {
        clearPaylensCachedAccessToken();
    } else if (isCachedTokenValid(cachedAccessToken)) {
        return Promise.resolve(cachedAccessToken);
    }

    if (tokenRequestPromise) {
        return tokenRequestPromise;
    }

    tokenRequestPromise = fetchPaylensEmbedTokenFromBff().finally(() => {
        tokenRequestPromise = null;
    });

    return tokenRequestPromise;
}

export function dispatchPaylensInboundMessageEvent(
    messageType: PaylensEmbedInboundDomEventDetail['messageType'],
    targetOrigin: string
): void {
    window.dispatchEvent(
        new CustomEvent<PaylensEmbedInboundDomEventDetail>(PAYLENS_EMBED_INBOUND_MESSAGE_DOM_EVENT, {
            detail: { messageType, targetOrigin },
        })
    );
}

function postAuthFailedToEmbed(targetWindow: Window, targetOrigin: string, message: string): void {
    const failedPayload: PaylensAuthFailedPostMessagePayload = {
        type: PAYLENS_AUTH_FAILED_MESSAGE_TYPE,
        message,
    };
    targetWindow.postMessage(failedPayload, targetOrigin);

    window.dispatchEvent(
        new CustomEvent<PaylensEmbedTokenDomEventDetail>(PAYLENS_EMBED_TOKEN_DOM_EVENT, {
            detail: { targetOrigin, hasToken: false },
        })
    );
}

/**
 * BFF'den embed token alır (gerekirse) ve bo-embed iframe'e paylens:token gönderir.
 */
export async function postPaylensTokenToIframe(
    targetWindow: Window | null | undefined,
    targetOrigin: string,
    options?: { reason?: PaylensEmbedTokenRequestReason; forceRefresh?: boolean }
): Promise<boolean> {
    if (!targetWindow || !targetOrigin) {
        return false;
    }

    try {
        const usedCache = !options?.forceRefresh && isCachedTokenValid(cachedAccessToken);
        const snapshot = await getValidPaylensEmbedToken({
            forceRefresh: options?.forceRefresh,
        });

        logPaylensEmbedTokenForTest(snapshot, {
            reason: options?.reason,
            fromCache: usedCache,
        });

        const payload: PaylensTokenPostMessagePayload = {
            type: PAYLENS_TOKEN_MESSAGE_TYPE,
            token: snapshot.token,
            expiresAt: snapshot.expiresAt,
            userId: snapshot.userId,
        };

        targetWindow.postMessage(payload, targetOrigin);

        window.dispatchEvent(
            new CustomEvent<PaylensEmbedTokenDomEventDetail>(PAYLENS_EMBED_TOKEN_DOM_EVENT, {
                detail: { targetOrigin, hasToken: true },
            })
        );

        return true;
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Access token not available';
        postAuthFailedToEmbed(targetWindow, targetOrigin, message);
        return false;
    }
}

export { getOriginFromIframeSrc };
